'use client';

import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AlertBanner } from '../../components/alert-banner';
import { MetaDefault } from '../../components/typography';
import { SettingsBooleanControl } from '../settings/SettingsBooleanControl';
import { SettingsCard } from '../settings/SettingsCard';
import { SettingsEditDialog } from '../settings/SettingsEditDialog';
import { SettingsRow } from '../settings/SettingsRow';
import { DeploymentDelayInputField } from './DeploymentDelayInputField';
import { useDeploymentDelayDraft } from './useDeploymentDelayDraft';

export type DeploymentDelayTicketingRetest = {
  checked: boolean;
  isAvailable: boolean;
  isUpdating: boolean;
  onCheckedChange: (checked: boolean) => void;
};

export type DeploymentDelaySettingsCardProps = {
  currentDelayHours: number | undefined;
  isReadOnly?: boolean;
  isUpdatingDelay: boolean;
  onSaveDelay: (hours: number) => Promise<boolean>;
  ticketingRetest?: DeploymentDelayTicketingRetest;
};

export function DeploymentDelaySettingsCard({
  currentDelayHours,
  isReadOnly,
  isUpdatingDelay,
  onSaveDelay,
  ticketingRetest,
}: DeploymentDelaySettingsCardProps) {
  const t = useTranslations('designSystem.deploymentDelay');
  const tCommon = useTranslations('designSystem.common');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const {
    draftDelay,
    parsedDraftHours,
    resetDraftFromCurrent,
    handleDelayChange,
    handleDelayBlur,
    handleDelayIntegerKeyDown,
    isSubmitDisabled,
  } = useDeploymentDelayDraft({ currentDelayHours, isReadOnly });

  return (
    <>
      <SettingsCard>
        <VStack gap={3} alignItems="stretch">
          <SettingsRow
            label={t('label')}
            value={t('hoursValue', { hours: currentDelayHours ?? '-' })}
            action={{
              label: tCommon('edit'),
              onClick: () => {
                resetDraftFromCurrent();
                setIsEditDialogOpen(true);
              },
              disabled: isUpdatingDelay,
            }}
            isReadOnly={isReadOnly}
          />
          <AlertBanner kind="info" description={t('alert')} />
        </VStack>
        {ticketingRetest ? (
          <SettingsRow
            label={t('ticketingRetestDelay.label')}
            description={
              <MetaDefault color="fg.neutral.subtle" whiteSpace="normal">
                {ticketingRetest.isAvailable
                  ? t('ticketingRetestDelay.description')
                  : t('ticketingRetestDelay.unavailableTooltip')}
              </MetaDefault>
            }
            control={
              <SettingsBooleanControl
                checked={ticketingRetest.checked}
                label={ticketingRetest.checked ? tCommon('yes') : tCommon('no')}
                disabled={
                  isReadOnly ||
                  isUpdatingDelay ||
                  ticketingRetest.isUpdating ||
                  !ticketingRetest.isAvailable
                }
                onCheckedChange={({ checked }) =>
                  ticketingRetest.onCheckedChange(checked)
                }
              />
            }
            isReadOnly={isReadOnly}
          />
        ) : null}
      </SettingsCard>

      <SettingsEditDialog
        open={isEditDialogOpen}
        title={t('editTitle')}
        onOpenChange={(open) => {
          if (!open) {
            resetDraftFromCurrent();
          }

          setIsEditDialogOpen(open);
        }}
        onSubmit={async () => {
          if (isReadOnly || parsedDraftHours === null) {
            return;
          }

          if (await onSaveDelay(parsedDraftHours)) {
            setIsEditDialogOpen(false);
          }
        }}
        loading={isUpdatingDelay}
        isSubmitDisabled={isSubmitDisabled}
        submitText={tCommon('saveChanges')}
      >
        <DeploymentDelayInputField
          draftDelay={draftDelay}
          onDraftDelayChange={handleDelayChange}
          onDraftDelayBlur={handleDelayBlur}
          onDelayIntegerKeyDown={handleDelayIntegerKeyDown}
        />
      </SettingsEditDialog>
    </>
  );
}
